<template>
  <div
    class="w-full border border-black/8 shdw flex items-center justify-between px-4 py-3 rounded-xl relative font-secondary text-gray-800 gap-x-5 cursor-pointer"
    :class="classes"
    @click="handleClick"
  >
    <div
      v-if="isAnnual"
      class="w-fit h-5 bg-red-500 rounded-tr-[10px] rounded-bl-[10px] text-white font-secondary text-[11px] leading-[12px] px-2.5 flex items-center justify-center absolute top-0 right-0 font-medium uppercase"
    >
      Annual &bull; Save additional {{ discount }}%
    </div>

    <div>
      <div class="text-[14px] leading-[20px]">{{ title }}</div>

      <div class="mb-1">
        <div
          v-if="isPAYG"
          class="flex flex-col text-[13px] leading-[20px] font-semibold"
        >
          <div>
            {{ paygPrices.copyright?.details?.renderedPrice }}
            {{ paygPrices.copyright?.details?.suffix }}
          </div>
          <div class="text-[11px] leading-[18px]">
            +
            <span v-if="isAnnual" class="text-red-500 line-through">
              {{ originalPAYGPrice }}
            </span>
            {{ paygPrices.subscription?.details?.renderedPrice }}
            {{ paygPrices.subscription?.details?.suffix }}
          </div>
        </div>

        <div v-else class="text-[13px] font-semibold">
          <span v-if="isAnnual" class="text-red-500 line-through">
            {{ originalBundlePrice }}
          </span>
          {{ lowestActivePlanItem?.prices[0].details?.renderedPrice }}
          {{ lowestActivePlanItem?.prices[0].details?.suffix }}
        </div>
      </div>
    </div>

    <Checkbox v-model="selected" @click.stop />
  </div>
</template>

<script setup lang="ts">
import {
  PlanItemStatus,
  PlanPriceType,
  type Plan,
} from '@/shared/graphql/legacy/generated'
import { useWebviewStore } from '@/shared/stores/webview'
import { formatPrice } from '@/shared/utils/money'
import { Checkbox } from '@dacr/ui'
import { computed } from 'vue'

const props = defineProps<{
  modelValue?: Plan
  plan: Plan
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', plan: Plan): void
}>()

const webviewStore = useWebviewStore()

const selected = computed({
  get() {
    return props.modelValue?.id === props.plan.id
  },
  set() {
    handleClick()
  },
})

const lowestActivePlanItem = computed(() =>
  props.plan.items.find(
    (item, index, arr) =>
      item.status === PlanItemStatus.Active || index === arr.length - 1
  )
)

const isAnnual = computed(
  () =>
    lowestActivePlanItem.value?.prices.find(
      (price) => price.type === PlanPriceType.Subscription
    )?.details?.billingCycleType === 'year'
)

const discount = computed(() => (props.plan.items.length > 1 ? 25 : 40))

const title = computed(() =>
  isAnnual.value ? 'Billed annually' : 'Billed monthly'
)

const originalPAYGPrice = computed(() => {
  const price = lowestActivePlanItem.value?.prices.find(
    (price) => price.type === PlanPriceType.Subscription
  )
  let amount = (price?.details?.amount || 0) / 100
  if (!webviewStore.isActive) {
    amount = amount / 12
  }
  return formatPrice({
    amount: Math.round(amount / (1 - discount.value / 100)),
    currency: price?.details?.currency,
    hideDecimals: true,
  })
})

const originalBundlePrice = computed(() => {
  const price = lowestActivePlanItem.value?.prices.find(
    (price) => price.type === PlanPriceType.Subscription
  )
  let amount = (price?.details?.amount || 0) / 100
  if (!webviewStore.isActive) {
    amount = amount / 12
  }
  return formatPrice({
    amount: Math.round(amount / (1 - discount.value / 100)),
    currency: price?.details?.currency,
    hideDecimals: true,
  })
})

const classes = computed(() => ({
  'pt-6 min-[360px]:pt-3': isAnnual.value,
  'border-green-500 bg-green-200/20 outline outline-green-500': selected.value,
  'hover:bg-gray-200/13': !selected.value,
}))

const isPAYG = computed(() => props.plan.items.length === 1)

const paygPrices = computed(() => {
  const subscription = props.plan.items[0].prices.find(
    (price) => price.type === PlanPriceType.Subscription
  )
  const copyright = props.plan.items[0].prices.find(
    (price) => price.type === PlanPriceType.File
  )
  return { subscription, copyright }
})

const handleClick = () => {
  emit('update:modelValue', props.plan)
}
</script>

<style scoped>
.shdw {
  box-shadow:
    0 4px 18px 0 rgba(0, 0, 0, 0.04),
    0 0.8px 2.93px 0 rgba(0, 0, 0, 0.02);
}
</style>
